import { GeneralSettingSchema } from '@blocksuite/affine/shared/services';
import { z } from 'zod';

export const BSEditorSettingSchema = GeneralSettingSchema;

export type FontFamily = 'Sans' | 'Serif' | 'Mono' | 'Custom';
export type EdgelessDefaultTheme = 'auto' | 'dark' | 'light' | 'specified';
export const newDocDateTitleFormatOptions = [
  'DD-MM-YYYY',
  'MM-DD-YYYY',
  'YYYY-MM-DD',
  'journal',
] as const;
export type NewDocDateTitleFormat =
  (typeof newDocDateTitleFormatOptions)[number];

export const pageWidthOptions = ['standard', 'wide', 'fullWidth'] as const;
export type PageWidth = (typeof pageWidthOptions)[number];

export const fontStyleOptions = [
  { key: 'Sans', value: 'var(--affine-font-sans-family)' },
  { key: 'Serif', value: 'var(--affine-font-serif-family)' },
  { key: 'Mono', value: 'var(--affine-font-mono-family)' },
  { key: 'Custom', value: 'var(--affine-font-sans-family)' },
] satisfies {
  key: FontFamily;
  value: string;
}[];

const AffineEditorSettingSchema = z.object({
  fontFamily: z.enum(['Sans', 'Serif', 'Mono', 'Custom']).default('Sans'),
  customFontFamily: z.string().default(''),
  fontSize: z.number().min(12).max(24).default(16),
  newDocDefaultMode: z.enum(['edgeless', 'page', 'ask']).default('page'),
  captureDocCreationLocation: z.boolean().default(true),
  autoTitleNewDocWithCurrentDate: z.boolean().default(false),
  newDocDateTitleFormat: z
    .enum(newDocDateTitleFormatOptions)
    .default('DD-MM-YYYY'),
  displayAddIconOption: z.boolean().default(true),
  fullWidthLayout: z.boolean().default(false),
  // Unset means "follow fullWidthLayout", so settings saved before Wide
  // existed keep their meaning. Write it through setDefaultPageWidth.
  defaultPageWidth: z.enum(pageWidthOptions).optional(),
  displayDocInfo: z.boolean().default(true),
  displayBiDirectionalLink: z.boolean().default(true),
  edgelessDefaultTheme: z
    .enum(['specified', 'dark', 'light', 'auto'])
    .default('specified'),
  openDocMode: z
    .enum([
      'open-in-active-view',
      'open-in-new-view',
      'open-in-new-tab',
      'open-in-center-peek',
    ])
    .default('open-in-active-view'),
  // linux only:
  enableMiddleClickPaste: z.boolean().default(false),
  codeBlockLineNumbers: z.boolean().default(true),
  // ids of workspaces hidden from the workspace switcher
  privateWorkspaces: z.array(z.string()).default([]),
  // tinykeys binding that opens the private workspace list, '' disables it
  privateWorkspacesShortcut: z.string().default('$mod+Shift+P'),
  // how long the workspace switcher must be held to open that list
  privateWorkspacesLongPressSeconds: z.number().min(0.5).max(10).default(5),
});

export const EditorSettingSchema = BSEditorSettingSchema.merge(
  AffineEditorSettingSchema
);

// oxlint-disable-next-line no-redeclare
export type EditorSettingSchema = z.infer<typeof EditorSettingSchema>;
